/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ReportDesigner.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Grid,
} from "@mui/material";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import MenuIcon from "@mui/icons-material/Menu";

import LeftSidebar from "./UnifiedLeftSidebar";
import Canvas from "./Canvas";
import PreviewModal from "./PreviewModal";
import TopToolbarNew from "./TopBarNew";
import { HandleApiActions } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import dynamicTemplateAction from "../../../redux/actions/dynamicTemplateAction";
import { useLocation, useNavigate } from "react-router-dom";
import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";

/* -------------------------------
   DEFAULT PAGE SETTINGS
-------------------------------- */
const initialPageSettings = {
  pageSize: "A4",
  orientation: "Portrait",
  marginTop: 20,
  marginRight: 20,
  marginBottom: 20,
  marginLeft: 20,
  padding: 16,
  backgroundColor: "#FFFFFF",
};

export default function ReportDesigner({ type, elements, onSave }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const templateId = queryParams.get("id");
  const [formStatus, setFormStatus] = useState(null);
  /* -------------------------------
     MULTI PAGE STATE
  -------------------------------- */
  const [pages, setPages] = useState([
    { elements: [], settings: initialPageSettings },
  ]);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  console.log(pages, "pagespages");
  const currentPage = pages[currentPageIndex];
  const canvasElements = currentPage?.elements;
  console.log(canvasElements, "canvasElements");
  const pageSettings = currentPage?.settings;
  const [selectedElement, setSelectedElement] = useState(null);
  const [checked, setChecked] = useState(false);
  /* -------------------------------
     OTHER STATE
  -------------------------------- */
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [zoom, setZoom] = useState(100);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [leftOpen, setLeftOpen] = useState(true);
  const [formType, setFormType] = useState(100);
  const [templateName, setTemplateName] = useState("New Template");
  const [currentFormId, setCurrentFormId] = useState(null);
  const initialTemplateRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  //useEfect to call handel load by id
  useEffect(() => {
    if (templateId) handleLoadById(templateId);
  }, [templateId]);

  useEffect(() => {
    if (type === "update" && elements) {
      const normalizedPages = Array.isArray(elements)
        ? elements.map((page) => ({
            ...page,
            elements: Array.isArray(page?.elements) ? page.elements : [],
          }))
        : [{ elements: [], settings: initialPageSettings }];

      setPages(JSON.parse(JSON.stringify(normalizedPages)));
    }
  }, [type]);

  const handleSave = () => {
    const normalizedPages = (Array.isArray(pages) ? pages : []).map((page) => {
      const safeElements = Array.isArray(page?.elements) ? page.elements : [];
      return {
        ...page,
        elements: safeElements.map((el) => ({
          ...el,
          properties: { ...el.properties },
        })),
      };
    });

    onSave?.(normalizedPages); // this calls parent
  };

  const resetTemplateState = () => {
    setPages([{ elements: [], settings: initialPageSettings }]);
    setTemplateName("New Template");
    setCurrentFormId(null);
    setCurrentPageIndex(0);
    setSelectedElementId(null);
  };

  //function to load data for update operation
  const handleLoadById = async (idStr) => {
    const id = Number(idStr);
    if (!id) return;

    try {
      const resp = await dispatch(
        HandleApiActions({
          ...dynamicTemplateAction.get_d_template,
          params: {
            id,
            page_number: 1,
            page_size: 25,
          },
        }),
      );

      // API error
      if (!resp || resp.error) {
        resetTemplateState();
        throw new Error(resp?.message || "Failed to load template");
      }

      const template = resp?.data;
      const data = Array.isArray(template) ? template[0] : template;

      if (!data) {
        resetTemplateState();
        throw new Error("Template data not found");
      }

      const {
        id: formId = null,
        name = "Loaded Template",
        fields = [],
        type,
      } = data;
      setFormType(type);
      // ✅ Normalize pages
      const normalizedPages = Array.isArray(fields)
        ? fields
        : [{ elements: [], settings: initialPageSettings }];

      setPages(JSON.parse(JSON.stringify(normalizedPages)));
      setTemplateName(name);
      setCurrentFormId(formId);
      setCurrentPageIndex(0);
      setSelectedElementId(null);
      initialTemplateRef.current = {
        templateName: name,
        formType: type,
        pages: JSON.parse(JSON.stringify(normalizedPages)),
      };
      setChecked(data?.status === 100 ? false : true);
      setFormStatus(data?.status);
    } catch (err) {
      console.error("handleLoadById error:", err);
    }
  };

  /* -------------------------------
     SEED INITIAL PAGE
  -------------------------------- */
  useEffect(() => {
    setPages((prev) => {
      if (prev[0]?.elements?.length) return prev;

      const now = Date.now();
      return [
        {
          elements: [
            {
              id: `heading-${now}`,
              type: "heading",
              x: 40,
              y: 40,
              width: 600,
              height: 40,
              properties: { text: "Form Title", fontSize: 20 },
            },
          ],
          settings: initialPageSettings,
        },
      ];
    });
  }, []);

  console.log(pages, "pages");
  /* -------------------------------
     DEFAULT PROPERTIES
  -------------------------------- */
  const getDefaultProperties = (type) => {
    switch (type) {
      case "text":
        return { text: "New Text", fontSize: 16 };
      case "heading":
        return { text: "New Heading", fontSize: 20 };
      case "table":
        return {
          rows: 2,
          columns: [
            { key: `col_${Date.now()}_1`, label: "Column 1", type: "default" },
            { key: `col_${Date.now()}_2`, label: "Column 2", type: "default" },
          ],
        };
      case "divider":
        return { color: "#CBD5E1", thickness: 2 };
      case "signature":
        return { label: "Signature", borderColor: "#CBD5E1" };
      case "radio":
      case "checkbox":
        return {
          label: "Select option",
          options: [
            { key: "opt1", label: "Option 1" },
            { key: "opt2", label: "Option 2" },
          ],
        };
      case "input":
        return {
          field_name: "New Field",
          sequence: "1",
          edit_type: 300,
          default_value: "",
          placeholder: "",
          inputType: "text",
          required: false,
        };
      default:
        return {};
    }
  };

  /* -------------------------------
     ELEMENT ACTIONS
  -------------------------------- */
  const handleAddElement = (type, pos) => {
    const newElement = {
      id: Date.now().toString(),
      type,
      x: pos?.x ?? 100,
      y: pos?.y ?? 100,
      width: type === "divider" ? 500 : 400,
      height: type === "divider" ? 2 : 40,
      properties: getDefaultProperties(type),
      edit_type: 300,
    };

    setPages((prev) =>
      prev.map((page, index) =>
        index === currentPageIndex
          ? {
              ...page,
              elements: [
                ...(Array.isArray(page?.elements) ? page.elements : []),
                newElement,
              ],
            }
          : page,
      ),
    );

    setSelectedElementId(newElement.id);
  };

  const updatePageElements = (elements, id, updates) => {
    const safeElements = Array.isArray(elements) ? elements : [];

    return safeElements.map((el) => {
      if (el.id === id) {
        return { ...el, ...updates };
      }
      return el;
    });
  };
  const updatePages = (pages, currentPageIndex, id, updates) => {
    return pages.map((page, index) => {
      if (index !== currentPageIndex) return page;

      return {
        ...page,
        elements: updatePageElements(page?.elements, id, updates),
      };
    });
  };
  const handleUpdateElement = (id, updates) => {
    console.log(id, "updates");
    console.log(updates, "updates");

    setPages((prev) => updatePages(prev, currentPageIndex, id, updates));
  };

  const handleUpdateProperties = (properties) => {
    if (!selectedElementId) return;
    handleUpdateElement(selectedElementId, { properties });
  };

  const deletePageElements = (elements, selectedElementId) => {
    const safeElements = Array.isArray(elements) ? elements : [];

    return safeElements.filter((el) => el.id !== selectedElementId);
  };

  const deleteFromPages = (pages, currentPageIndex, selectedElementId) => {
    return pages.map((page, index) => {
      if (index !== currentPageIndex) return page;

      return {
        ...page,
        elements: deletePageElements(page?.elements, selectedElementId),
      };
    });
  };
  const handleDeleteElement = () => {
    if (!selectedElementId) return;

    setPages((prev) =>
      deleteFromPages(prev, currentPageIndex, selectedElementId),
    );

    setSelectedElementId(null);
  };
  //for delete by kayboard delete

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Avoid deleting while typing in inputs
      const tag = e.target.tagName?.toLowerCase();
      const isTyping =
        tag === "input" || tag === "textarea" || e.target.isContentEditable;

      if (isTyping) return;

      if ((e.key === "Delete" || e.key === "Backspace") && selectedElementId) {
        e.preventDefault();
        handleDeleteElement();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedElementId, currentPageIndex]);

  /* -------------------------------
     PAGE SETTINGS
  -------------------------------- */
  const handlePageSettingsChange = (newSettings) => {
    setPages((prev) =>
      prev.map((page, index) =>
        index === currentPageIndex
          ? { ...page, settings: { ...newSettings } }
          : page,
      ),
    );
  };

  /* -------------------------------
     ADD PAGE
  -------------------------------- */
  const handleAddPage = () => {
    setPages((prev) => [
      ...prev,
      { elements: [], settings: initialPageSettings },
    ]);
    setCurrentPageIndex(pages.length);
    setSelectedElementId(null);
  };

  //function to add dynamic form
  const handleCreateDT = (index) => {
    const reqObj = {
      name: templateName,
      type: parseInt(formType),
      fields: pages,
      headers: [{}],
      footers: [{}],
      signatures: [{}],
      status: index === 1 ? 200 : 100,
      remplate_tag: "NEW",
    };
    console.log("ReqObj For DT", reqObj);
    dispatch(
      HandleApiActions({
        ...dynamicTemplateAction.add_d_template,
        params: reqObj,
        show_toast: true,
      }),
    )?.then((resp) => {
      if (resp?.error === false) {
        dispatch(
          HandleApiActions({
            ...dynamicTemplateAction.get_d_template,
            params: {
              page_size: 25,
              page_number: 1,
            },
          }),
        );
        // onClose();
      }
      navigate("/dashboard/admin-settings/ddh_repository?form");
    });
  };

  const handleUpdateDT = () => {
    const safePages = JSON.parse(JSON.stringify(pages));

    const reqObj = {
      id: Number.parseInt(templateId),
      fields: safePages,
      headers: [{}],
      footers: [{}],
      signatures: [{}],
      template_tag: "NEW",
    };

    dispatch(
      HandleApiActions({
        ...dynamicTemplateAction?.update_d_template,
        params: reqObj,
        show_toast: true,
      }),
    )?.then((response) => {
      initialTemplateRef.current = {
        templateName,
        formType,
        pages: safePages,
      };
      dispatch(
        HandleApiActions({
          ...dynamicTemplateAction.get_d_template,
          params: {
            page_size: 25,
            page_number: 1,
          },
        }),
      );
    });
  };

  //useeffect to add element on selection
  useEffect(() => {
    if (!selectedElementId) {
      setSelectedElement(null);
      return;
    }

    const el = canvasElements?.find((e) => e.id === selectedElementId);
    setSelectedElement(el || null);
  }, [selectedElementId, canvasElements]);

  const patchElements = (elements, selectedElementId, updater) => {
    const safeElements = Array.isArray(elements) ? elements : [];

    return safeElements.map((el) => {
      if (el.id !== selectedElementId) return el;

      return typeof updater === "function" ? updater(el) : updater;
    });
  };

  const patchPages = (
    prevPages,
    currentPageIndex,
    selectedElementId,
    updater,
    setSelectedElement,
  ) => {
    return prevPages.map((page, index) => {
      if (index !== currentPageIndex) return page;

      const newElements = patchElements(
        page?.elements,
        selectedElementId,
        updater,
      );

      const updatedEl = newElements.find((el) => el.id === selectedElementId);

      setSelectedElement(updatedEl || null);

      return { ...page, elements: newElements };
    });
  };
  const patchSelectedElement = (updater) => {
    setPages((prevPages) =>
      patchPages(
        prevPages,
        currentPageIndex,
        selectedElementId,
        updater,
        setSelectedElement,
      ),
    );
  };

  const updateOutsideProp = (key, value) => {
    if (!selectedElementId) return;

    patchSelectedElement((el) => ({
      ...el,
      [key]: value,
    }));
  };

  const handleUpdateStatusChange = (data) => {
    const newStatus = checked ? 100 : 200;
    const statusData = {
      id: Number.parseInt(data),
      status: newStatus,
    };

    // Toggle checked state
    setChecked(!checked);
    dispatch(
      HandleApiActions({
        ...dynamicTemplateAction.update_d_status_template,
        params: statusData,
        show_toast: true,
      }),
    )?.then(() => {
      dispatch(
        HandleApiActions({
          ...dynamicTemplateAction.get_d_template,
          params: {
            page_size: 25,
            page_number: 1,
          },
        }),
      );
    });
  };
  /* -------------------------------
     RENDER
  -------------------------------- */
  //function for pagination change
  const handlePageChange = (value) => {
    if (value - 1 !== currentPageIndex) {
      setCurrentPageIndex(value - 1);
    }
  };

  console.log(pages?.length, "pages length");
  console.log(currentPageIndex, "currentPageIndex");

  const handleDelete = () => {
    //delete selected page
    const nonDeletedData = pages?.filter(
      (data, index) => index !== currentPageIndex,
    );
    console.log(nonDeletedData, "nonDeletedData");
    setPages(nonDeletedData);
    setCurrentPageIndex(currentPageIndex - 1);
  };

  const isUpdateDisabled = useMemo(() => {
    if (!templateId) return true;
    const initial = initialTemplateRef.current;
    if (!initial) return true;
    const sameName = (initial.templateName || "") === (templateName || "");
    const sameType = String(initial.formType) === String(formType);
    const samePages =
      JSON.stringify(initial.pages || []) === JSON.stringify(pages || []);
    return sameName && sameType && samePages;
  }, [templateId, templateName, formType, pages]);
  return (
    <Box sx={{ height: "100vh", bgcolor: "#F4F6F8" }}>
      <DndProvider backend={HTML5Backend} sx={{ bgcolor: "#F4F6F8" }}>
        <Grid
          container
          sx={{
            height: "100vh",
            overflow: "hidden",
            bgcolor: "#F4F6F8",
            // gap: .5,
          }}
        >
          {/* LEFT SIDEBAR */}
          <Grid
            item
            xs={leftOpen ? 2.7 : 0.5}
            sx={{
              height: "100%",
              overflowY: "auto",
            }}
          >
            {leftOpen ? (
              <LeftSidebar
                open={leftOpen}
                onClose={() => setLeftOpen(false)}
                onAddElement={handleAddElement}
                pageSettings={pageSettings}
                onPageSettingsChange={handlePageSettingsChange}
                onUpdateProperties={handleUpdateProperties}
                onUpdateElement={updateOutsideProp}
                onDeleteElement={handleDeleteElement}
                selectedElement={canvasElements?.find(
                  (e) => e.id === selectedElementId,
                )}
              />
            ) : (
              <Box sx={{ p: 1 }}>
                <IconButton onClick={() => setLeftOpen(true)}>
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Grid>

          {/* MAIN CONTENT */}
          <Grid
            item
            xs={leftOpen ? 9.3 : 11.5}
            sx={{
              height: "100%",
              //  border:"2px solid yellow",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* TOOLBAR */}
            {type !== "update" && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  m: ".5rem",
                  zIndex: 10,
                }}
              >
                <TopToolbarNew
                  templateName={templateName}
                  setTemplateName={setTemplateName}
                  handleSaveTemplate={handleCreateDT}
                  onPreview={() => setIsPreviewOpen(true)}
                  onToggleLeft={() => setLeftOpen((s) => !s)}
                  handleAddPage={handleAddPage}
                  setFormType={setFormType}
                  formType={formType}
                  handleCreateDT={handleCreateDT}
                  templateId={templateId}
                  handleUpdateDT={handleUpdateDT}
                  isUpdateDisabled={isUpdateDisabled}
                  handleUpdateStatusChange={handleUpdateStatusChange}
                  checked={checked}
                  formStatus={formStatus}
                />
              </Box>
            )}

            {/* CANVAS SCROLL AREA */}
            <Box
              sx={{
                flex: 1,
                height: "100%",
                overflowY: "auto", //shows scrollbar only when needed
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                bgcolor: "#F4F6F8",
                /*Prevent layout shift when scrollbar appears */
                scrollbarGutter: "stable",
                /* Optional: nice thin scrollbar */
                scrollbarWidth: "thin", // Firefox
                "&::-webkit-scrollbar": {
                  width: "8px", // Chrome / Edge
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box /> {/* empty left spacer */}
                  <CustomPaginationV2
                    page={currentPageIndex + 1}
                    rowsPerPage={1}
                    onPageChange={handlePageChange}
                    totalItems={pages?.length}
                  />
                  <Box>
                    {type === "update" && (
                      <CustomButtonV2
                        type="submit"
                        size="small"
                        btnName="Save"
                        handleOnClick={handleSave}
                      />
                    )}
                  </Box>
                  <Box>
                    {currentPageIndex !== 0 && (
                      <IconButton size="small" onClick={handleDelete}>
                        <DeleteIcon sx={{ color: "black", fontSize: "1rem" }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    minHeight: "calc(100vh - 160px)",
                    display: "flex",
                    justifyContent: "center",
                    bgcolor: "#fff",
                    // boxShadow: 3,
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Canvas
                    elements={canvasElements}
                    selectedElementId={selectedElementId}
                    onSelectElement={(id) => {
                      setSelectedElementId(id);
                    }}
                    onUpdateElement={handleUpdateElement}
                    onAddElement={handleAddElement}
                    pageSettings={pageSettings}
                    zoom={zoom}
                    onZoomChange={setZoom}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* PREVIEW */}
          <PreviewModal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            pages={pages}
          />

          {/* SNACKBAR */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert severity={snackbar.severity} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Grid>
      </DndProvider>
    </Box>
  );
}
